import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LoginStatus } from '@ndvr/auth-api';

import { Spinner, VerticalLayout } from 'widgets';

import { useAuthContext } from './Auth';
import { useAuthorizeCognitoOAuthCode } from './auth-queries';
import { AuthenticationPage } from './AuthenticationPage';

const INTERNAL_ERROR_MESSAGE = 'Internal Error. Please try again later.';
const UNVERIFIED_ERROR_MESSAGE = 'Failed to login, please contact support.';
const LOGIN_TITLE = 'Welcome back, sign in to your account.';

export function Authorize(): JSX.Element {
    const [searchParams] = useSearchParams();
    const code = searchParams.get('code');
    const handledCode = useRef<string | null>(null);

    const { setAuthenticatedUser, isAuthenticated } = useAuthContext();
    const navigate = useNavigate();

    const { mutate, isPending } = useAuthorizeCognitoOAuthCode();

    const redirectToLogin = useCallback(
        (message: string): void => {
            navigate('/login', { replace: true, state: { loginError: message } });
        },
        [navigate],
    );

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/home');
            return;
        }

        if (!code) {
            return;
        }

        if (handledCode.current === code) {
            return;
        }
        handledCode.current = code;

        mutate(code, {
            onError: () => {
                redirectToLogin(INTERNAL_ERROR_MESSAGE);
            },
            onSuccess(loginResponse) {
                if (!loginResponse) {
                    redirectToLogin(INTERNAL_ERROR_MESSAGE);
                    return;
                }

                if (loginResponse.status === LoginStatus.UNVERIFIED) {
                    redirectToLogin(UNVERIFIED_ERROR_MESSAGE);
                    return;
                }

                if (!loginResponse.personOrgGUID) {
                    redirectToLogin(INTERNAL_ERROR_MESSAGE);
                    return;
                }

                setAuthenticatedUser(loginResponse.personOrgGUID);
                navigate('/home');
            },
        });
    }, [code, navigate, isAuthenticated, setAuthenticatedUser, mutate, redirectToLogin]);

    return (
        <AuthenticationPage title={LOGIN_TITLE}>
            <VerticalLayout $gap={16} $columnCenter>
                {isPending && (
                    <div data-testid="authorize-loading-spinner">
                        <Spinner size="large" />
                    </div>
                )}
            </VerticalLayout>
        </AuthenticationPage>
    );
}
